/**
 * VideoWallEditor.js - Visual editor for arranging displays and deploying images to video wall
 */

import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from '../../axiosInstance';

const VideoWallEditor = ({ displays, videoWalls, onRefresh }) => {
  const [selectedWall, setSelectedWall] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [wallName, setWallName] = useState('');
  const [wallDescription, setWallDescription] = useState('');
  const [canvasSize, setCanvasSize] = useState({ width: 3840, height: 2160 });
  const [layoutItems, setLayoutItems] = useState([]);
  const [draggingItem, setDraggingItem] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const canvasRef = useRef(null);

  const SCALE = 0.2; // Scale factor for display on canvas (20% of actual size)

  // Load selected wall
  useEffect(() => {
    if (selectedWall) {
      loadWall(selectedWall._id);
    }
  }, [selectedWall]);

  const loadWall = async (wallId) => {
    try {
      const response = await axiosInstance.get(`/video-sync/video-walls/${wallId}`);
      const wall = response.data.videoWall;
      setLayoutItems(wall.layout || []);
      setCanvasSize(wall.canvas);
    } catch (error) {
      console.error('[VideoWall] Load error:', error);
    }
  };

  const createWall = async () => {
    if (!wallName.trim()) {
      alert('Please enter a wall name');
      return;
    }

    try {
      const response = await axiosInstance.post('/video-sync/video-walls', {
        name: wallName,
        description: wallDescription,
        canvas: canvasSize,
        layout: [],
      });

      setShowCreateModal(false);
      setWallName('');
      setWallDescription('');
      onRefresh();
      setSelectedWall(response.data.videoWall);
    } catch (error) {
      console.error('[VideoWall] Create error:', error);
      alert('Failed to create video wall');
    }
  };

  const addDisplayToWall = (display) => {
    if (!selectedWall) {
      alert('Please select or create a video wall first');
      return;
    }

    // Check if display is already in the layout
    if (layoutItems.find(item => item.display._id === display._id)) {
      alert('Display already added to this wall');
      return;
    }

    // Use actual display resolution from database
    const newItem = {
      display: display,
      position: {
        x: 100,
        y: 100,
      },
      size: {
        width: display.resolution?.width || 1920,
        height: display.resolution?.height || 1080,
      },
      rotation: 0,
    };

    setLayoutItems([...layoutItems, newItem]);
  };

  const removeDisplayFromWall = (displayId) => {
    setLayoutItems(layoutItems.filter(item => item.display._id !== displayId));
  };

  const startDrag = (item, e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const offsetX = e.clientX - rect.left - item.position.x * SCALE;
    const offsetY = e.clientY - rect.top - item.position.y * SCALE;

    setDraggingItem({ item, offsetX, offsetY });
  };

  const handleDrag = (e) => {
    if (!draggingItem) return;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left - draggingItem.offsetX) / SCALE;
    const y = (e.clientY - rect.top - draggingItem.offsetY) / SCALE;

    setLayoutItems(layoutItems.map(item =>
      item.display._id === draggingItem.item.display._id
        ? { ...item, position: { x: Math.max(0, x), y: Math.max(0, y) } }
        : item
    ));
  };

  const endDrag = () => {
    setDraggingItem(null);
  };

  const saveLayout = async () => {
    if (!selectedWall) return;

    try {
      await axiosInstance.put(`/video-sync/video-walls/${selectedWall._id}`, {
        layout: layoutItems.map(item => ({
          display: item.display._id,
          position: item.position,
          size: item.size,
          rotation: item.rotation,
        })),
        canvas: canvasSize,
      });

      alert('Layout saved successfully!');
      onRefresh();
    } catch (error) {
      console.error('[VideoWall] Save layout error:', error);
      alert('Failed to save layout');
    }
  };

  const autoArrangeGrid = async () => {
    if (!selectedWall) return;

    const rows = prompt('How many rows?', '2');
    const cols = prompt('How many columns?', '2');

    if (!rows || !cols) return;

    try {
      await axiosInstance.post(`/video-sync/video-walls/${selectedWall._id}/auto-arrange`, {
        rows: parseInt(rows),
        cols: parseInt(cols),
      });

      loadWall(selectedWall._id);
      alert('Displays arranged in grid!');
    } catch (error) {
      console.error('[VideoWall] Auto-arrange error:', error);
      alert('Failed to auto-arrange');
    }
  };

  const deleteWall = async () => {
    if (!selectedWall) return;

    if (!window.confirm(`Are you sure you want to delete "${selectedWall.name}"? This cannot be undone.`)) {
      return;
    }

    try {
      await axiosInstance.delete(`/video-sync/video-walls/${selectedWall._id}`);
      alert('Video wall deleted successfully!');
      setSelectedWall(null);
      setLayoutItems([]);
      onRefresh();
    } catch (error) {
      console.error('[VideoWall] Delete error:', error);
      alert('Failed to delete video wall');
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImageFile(file);

    // Preview image
    const reader = new FileReader();
    reader.onload = (event) => {
      setPreviewImage(event.target.result);
    };
    reader.readAsDataURL(file);
  };

  const deployImage = async () => {
    if (!selectedWall || !imageFile) {
      alert('Please select a video wall and upload an image');
      return;
    }

    if (layoutItems.length === 0) {
      alert('Please add displays to the wall first');
      return;
    }

    try {
      // Upload image first
      const formData = new FormData();
      formData.append('video', imageFile);

      const uploadResponse = await axiosInstance.post('/video-sync/videos/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const imagePath = uploadResponse.data.filePath;

      // Deploy to wall
      const deployResponse = await axiosInstance.post(
        `/video-sync/video-walls/${selectedWall._id}/deploy-image`,
        { imagePath }
      );

      const { deploymentResults } = deployResponse.data;
      const successCount = deploymentResults.filter(r => r.success).length;
      const failCount = deploymentResults.filter(r => !r.success).length;

      alert(
        `Image deployed to video wall!\n\n` +
        `✅ Success: ${successCount}\n` +
        `❌ Failed: ${failCount}`
      );

      onRefresh();
    } catch (error) {
      console.error('[VideoWall] Deploy image error:', error);
      alert('Failed to deploy image: ' + (error.response?.data?.error || error.message));
    }
  };

  return (
    <div className="video-wall-editor">
      <div className="editor-header">
        <h2>Video Wall Editor</h2>
        <p className="subtitle">Arrange displays and deploy images across your video wall</p>
      </div>

      {/* Video Wall Selection */}
      <div className="wall-selector">
        <select
          value={selectedWall?._id || ''}
          onChange={(e) => {
            const wall = videoWalls.find(w => w._id === e.target.value);
            setSelectedWall(wall);
          }}
        >
          <option value="">-- Select Video Wall --</option>
          {videoWalls.map(wall => (
            <option key={wall._id} value={wall._id}>
              {wall.name} ({wall.layout.length} displays)
            </option>
          ))}
        </select>

        <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
          + Create New Wall
        </button>

        {selectedWall && (
          <button className="btn-danger" onClick={deleteWall}>
            🗑️ Delete Wall
          </button>
        )}
      </div>

      {selectedWall && (
        <div className="editor-workspace">
          {/* Left Sidebar - Display List */}
          <div className="display-palette">
            <h3>Available Displays</h3>
            <div className="display-list">
              {displays.map(display => (
                <div key={display._id} className="palette-display">
                  <div className="display-info">
                    <div className="display-name">{display.name}</div>
                    <div className="display-ip">{display.ipAddress}</div>
                    <div className="display-res">
                      {display.resolution?.width || 1920} × {display.resolution?.height || 1080}
                    </div>
                  </div>
                  <button
                    className="btn-add"
                    onClick={() => addDisplayToWall(display)}
                    disabled={layoutItems.find(item => item.display._id === display._id)}
                  >
                    {layoutItems.find(item => item.display._id === display._id) ? '✓ Added' : '+ Add'}
                  </button>
                </div>
              ))}
            </div>

            <div className="controls">
              <button className="btn-secondary" onClick={autoArrangeGrid}>
                Auto-Arrange Grid
              </button>
              <button className="btn-success" onClick={saveLayout}>
                💾 Save Layout
              </button>
            </div>
          </div>

          {/* Center - Canvas */}
          <div className="canvas-container">
            <div className="canvas-header">
              <h3>{selectedWall.name}</h3>
              <div className="canvas-info">
                Canvas: {canvasSize.width} × {canvasSize.height} | Displays: {layoutItems.length}
              </div>
            </div>

            <div
              ref={canvasRef}
              className="canvas"
              style={{
                width: `${canvasSize.width * SCALE}px`,
                height: `${canvasSize.height * SCALE}px`,
                backgroundImage: previewImage ? `url(${previewImage})` : 'none',
                backgroundSize: 'cover',
              }}
              onMouseMove={handleDrag}
              onMouseUp={endDrag}
              onMouseLeave={endDrag}
            >
              {layoutItems.map((item, index) => (
                <div
                  key={item.display._id}
                  className="canvas-display"
                  style={{
                    left: `${item.position.x * SCALE}px`,
                    top: `${item.position.y * SCALE}px`,
                    width: `${item.size.width * SCALE}px`,
                    height: `${item.size.height * SCALE}px`,
                    transform: `rotate(${item.rotation}deg)`,
                    cursor: draggingItem ? 'grabbing' : 'grab',
                  }}
                  onMouseDown={(e) => startDrag(item, e)}
                >
                  <div className="display-label">
                    {item.display.name}
                    <div className="display-resolution">
                      {item.size.width} × {item.size.height}
                    </div>
                  </div>
                  <button
                    className="remove-btn"
                    onClick={() => removeDisplayFromWall(item.display._id)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Right Sidebar - Image Upload */}
          <div className="image-upload-panel">
            <h3>Deploy Image</h3>

            <div className="upload-area">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                id="wall-image-upload"
                style={{ display: 'none' }}
              />
              <label htmlFor="wall-image-upload" className="upload-label">
                {previewImage ? (
                  <img src={previewImage} alt="Preview" className="preview-thumb" />
                ) : (
                  <div className="upload-placeholder">
                    <span>📷</span>
                    <p>Click to upload image</p>
                  </div>
                )}
              </label>
            </div>

            {imageFile && (
              <div className="file-info">
                <p><strong>{imageFile.name}</strong></p>
                <p>{(imageFile.size / 1024 / 1024).toFixed(2)} MB</p>
              </div>
            )}

            <button
              className="btn-deploy"
              onClick={deployImage}
              disabled={!imageFile || layoutItems.length === 0}
            >
              🚀 Deploy to Wall
            </button>

            <div className="deployment-info">
              <h4>How it works:</h4>
              <ol>
                <li>Arrange your displays on the canvas</li>
                <li>Upload a large image</li>
                <li>Click "Deploy" to slice and push to displays</li>
                <li>Each display shows its portion of the image</li>
              </ol>
            </div>
          </div>
        </div>
      )}

      {/* Create Wall Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Create Video Wall</h2>
            <button className="close-btn" onClick={() => setShowCreateModal(false)}>×</button>

            <div className="form-group">
              <label>Wall Name *</label>
              <input
                type="text"
                value={wallName}
                onChange={(e) => setWallName(e.target.value)}
                placeholder="e.g., Front Office Wall"
              />
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                value={wallDescription}
                onChange={(e) => setWallDescription(e.target.value)}
                placeholder="Optional description..."
                rows="2"
              />
            </div>

            <div className="form-actions">
              <button className="btn-secondary" onClick={() => setShowCreateModal(false)}>
                Cancel
              </button>
              <button className="btn-primary" onClick={createWall}>
                Create Wall
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx="true">{`
        .video-wall-editor {
          padding: 20px;
        }

        .editor-header {
          margin-bottom: 20px;
        }

        .editor-header h2 {
          font-size: 28px;
          margin-bottom: 10px;
        }

        .subtitle {
          color: #666;
          font-size: 14px;
        }

        .wall-selector {
          display: flex;
          gap: 10px;
          margin-bottom: 20px;
        }

        .wall-selector select {
          flex: 1;
          padding: 10px;
          border: 2px solid #e0e0e0;
          border-radius: 4px;
          font-size: 14px;
        }

        .editor-workspace {
          display: flex;
          gap: 20px;
          height: calc(100vh - 280px);
        }

        .display-palette {
          width: 250px;
          background: #f8f9fa;
          border-radius: 8px;
          padding: 15px;
          overflow-y: auto;
        }

        .display-palette h3 {
          font-size: 16px;
          margin-bottom: 15px;
        }

        .display-list {
          display: flex;
          flex-direction: column;
          gap: 10px;
          margin-bottom: 20px;
        }

        .palette-display {
          background: white;
          border: 1px solid #e0e0e0;
          border-radius: 6px;
          padding: 10px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .display-name {
          font-size: 13px;
          font-weight: 600;
        }

        .display-ip {
          font-size: 11px;
          color: #666;
        }

        .display-res {
          font-size: 10px;
          color: #3498db;
          font-weight: 600;
          margin-top: 2px;
        }

        .btn-add {
          padding: 4px 8px;
          background: #3498db;
          color: white;
          border: none;
          border-radius: 4px;
          font-size: 11px;
          cursor: pointer;
        }

        .btn-add:disabled {
          background: #27ae60;
          cursor: not-allowed;
        }

        .controls {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .canvas-container {
          flex: 1;
          background: white;
          border-radius: 8px;
          padding: 20px;
          overflow: auto;
        }

        .canvas-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 15px;
        }

        .canvas-info {
          font-size: 12px;
          color: #666;
        }

        .canvas {
          position: relative;
          background: #222;
          border: 2px solid #e0e0e0;
          background-size: 20px 20px;
          background-image:
            linear-gradient(to right, #333 1px, transparent 1px),
            linear-gradient(to bottom, #333 1px, transparent 1px);
        }

        .canvas-display {
          position: absolute;
          background: rgba(52, 152, 219, 0.3);
          border: 2px solid #3498db;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          user-select: none;
        }

        .canvas-display:hover {
          background: rgba(52, 152, 219, 0.5);
          border-color: #2980b9;
        }

        .display-label {
          color: white;
          font-size: 11px;
          font-weight: 600;
          text-align: center;
          text-shadow: 1px 1px 2px rgba(0,0,0,0.8);
        }

        .display-resolution {
          font-size: 9px;
          margin-top: 2px;
          opacity: 0.9;
        }

        .remove-btn {
          position: absolute;
          top: 2px;
          right: 2px;
          width: 20px;
          height: 20px;
          background: #e74c3c;
          color: white;
          border: none;
          border-radius: 50%;
          cursor: pointer;
          font-size: 14px;
          line-height: 1;
        }

        .image-upload-panel {
          width: 300px;
          background: #f8f9fa;
          border-radius: 8px;
          padding: 15px;
          overflow-y: auto;
        }

        .image-upload-panel h3 {
          font-size: 16px;
          margin-bottom: 15px;
        }

        .upload-area {
          margin-bottom: 15px;
        }

        .upload-label {
          display: block;
          cursor: pointer;
          border: 2px dashed #3498db;
          border-radius: 8px;
          padding: 20px;
          text-align: center;
          background: white;
        }

        .upload-label:hover {
          border-color: #2980b9;
          background: #e3f2fd;
        }

        .upload-placeholder span {
          font-size: 48px;
          display: block;
          margin-bottom: 10px;
        }

        .upload-placeholder p {
          font-size: 13px;
          color: #666;
        }

        .preview-thumb {
          max-width: 100%;
          max-height: 200px;
          border-radius: 4px;
        }

        .file-info {
          background: white;
          padding: 10px;
          border-radius: 6px;
          margin-bottom: 15px;
        }

        .file-info p {
          font-size: 12px;
          margin: 4px 0;
        }

        .btn-deploy {
          width: 100%;
          padding: 12px;
          background: #27ae60;
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          margin-bottom: 20px;
        }

        .btn-deploy:hover {
          background: #229954;
        }

        .btn-deploy:disabled {
          background: #95a5a6;
          cursor: not-allowed;
        }

        .deployment-info {
          background: white;
          padding: 15px;
          border-radius: 6px;
        }

        .deployment-info h4 {
          font-size: 13px;
          margin-bottom: 10px;
        }

        .deployment-info ol {
          margin-left: 20px;
        }

        .deployment-info li {
          font-size: 12px;
          color: #666;
          margin-bottom: 6px;
        }

        /* Modal Styles */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .modal-content {
          background: white;
          border-radius: 8px;
          padding: 30px;
          width: 500px;
          max-width: 90%;
          position: relative;
        }

        .close-btn {
          position: absolute;
          top: 15px;
          right: 15px;
          background: none;
          border: none;
          font-size: 24px;
          cursor: pointer;
          color: #666;
        }

        .form-group {
          margin-bottom: 20px;
        }

        .form-group label {
          display: block;
          font-weight: 600;
          margin-bottom: 8px;
          font-size: 14px;
        }

        .form-group input,
        .form-group textarea {
          width: 100%;
          padding: 10px;
          border: 2px solid #e0e0e0;
          border-radius: 4px;
          font-size: 14px;
          font-family: inherit;
        }

        .form-actions {
          display: flex;
          gap: 10px;
          justify-content: flex-end;
        }

        .btn-primary,
        .btn-secondary,
        .btn-success,
        .btn-danger {
          padding: 10px 20px;
          border: none;
          border-radius: 4px;
          font-size: 14px;
          cursor: pointer;
        }

        .btn-primary {
          background: #3498db;
          color: white;
        }

        .btn-secondary {
          background: #6c757d;
          color: white;
        }

        .btn-success {
          background: #27ae60;
          color: white;
        }

        .btn-danger {
          background: #e74c3c;
          color: white;
        }

        .btn-danger:hover {
          background: #c0392b;
        }
      `}</style>
    </div>
  );
};

export default VideoWallEditor;
